const roundCurrency = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return parseFloat(parsed.toFixed(2));
};

export const normalizeVatRate = (rawRate) => {
  const parsedRate = Number.parseFloat(rawRate);
  if (!Number.isFinite(parsedRate) || parsedRate < 0) return null;
  return parsedRate > 1 ? parsedRate / 100 : parsedRate;
};

const calculateVatFromGross = (grossAmount, vatRate) => {
  const amount = Number(grossAmount);
  const rate = normalizeVatRate(vatRate);
  if (!Number.isFinite(amount) || amount <= 0 || !Number.isFinite(rate) || rate <= 0) {
    return 0;
  }
  return amount - (amount / (1 + rate));
};

const calculateGrossFromVat = (vatAmount, vatRate) => {
  const amount = Number(vatAmount);
  const rate = normalizeVatRate(vatRate);
  if (!Number.isFinite(amount) || amount < 0 || !Number.isFinite(rate) || rate <= 0) {
    return 0;
  }
  return amount * ((1 + rate) / rate);
};

const normalizeTransactionType = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized === 'expense' ? 'expenses' : normalized;
};

const normalizeCategoryKey = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[\s_-]+/g, '');

const NON_TAXABLE_INCOME_CATEGORY_KEYS = new Set([
  'giftcard',
  'giftvoucher'
]);

const isNonTaxableIncomeCategory = (category) => (
  NON_TAXABLE_INCOME_CATEGORY_KEYS.has(normalizeCategoryKey(category))
);

const NESTED_BREAKDOWN_TOLERANCE = 0.02;

const shouldUseNestedVatEntries = (transaction, entries) => {
  if (!Array.isArray(entries) || entries.length === 0) return false;
  if (transaction?.isSplitTransaction === true) return true;

  const explicitVatAmount = Number.parseFloat(transaction?.vatAmount);
  const nestedVatAmount = entries.reduce((sum, entry) => sum + (Number(entry?.vatAmount) || 0), 0);
  if (Number.isFinite(explicitVatAmount) && explicitVatAmount > 0 && nestedVatAmount <= 0) {
    return false;
  }

  const distinctCategoryKeys = new Set(
    entries
      .map((entry) => normalizeCategoryKey(entry?.category))
      .filter(Boolean)
  );
  const distinctRates = new Set(
    entries
      .map((entry) => normalizeVatRate(entry?.rate))
      .filter((rate) => Number.isFinite(rate))
      .map((rate) => rate.toFixed(6))
  );

  if (distinctCategoryKeys.size > 1 || distinctRates.size > 1) {
    return true;
  }

  const transactionAmount = Number.parseFloat(transaction?.amount);
  if (!Number.isFinite(transactionAmount) || transactionAmount <= 0) {
    return true;
  }

  const nestedGrossAmount = entries.reduce((sum, entry) => sum + (Number(entry?.grossAmount) || 0), 0);
  if (Math.abs(nestedGrossAmount - transactionAmount) > NESTED_BREAKDOWN_TOLERANCE) {
    return false;
  }

  const normalizedTransactionCategory = normalizeCategoryKey(transaction?.category);
  if (
    distinctCategoryKeys.size === 1 &&
    normalizedTransactionCategory &&
    !distinctCategoryKeys.has(normalizedTransactionCategory)
  ) {
    return false;
  }

  return true;
};

const shouldIncludeEntry = (entry, includeZeroVatEntries) => {
  const grossAmount = Number(entry?.grossAmount);
  const vatAmount = Number(entry?.vatAmount);
  const rate = normalizeVatRate(entry?.rate);

  if (!Number.isFinite(grossAmount) || grossAmount <= 0) return false;
  if (Number.isFinite(vatAmount) && vatAmount > 0) return true;
  return includeZeroVatEntries && Number.isFinite(vatAmount) && vatAmount === 0 && (
    (Number.isFinite(rate) && rate === 0) ||
    entry?.rate === null ||
    entry?.rate === undefined ||
    entry?.rate === ''
  );
};

const finalizeGroupedEntries = (groupedEntries, includeZeroVatEntries, resolveTaxComponentDescription) =>
  Array.from(groupedEntries.values())
    .map((entry) => ({
      category: entry.category,
      rate: entry.rate,
      vatAmount: roundCurrency(entry.vatAmount),
      grossAmount: roundCurrency(entry.grossAmount),
      description: entry.description || (
        typeof resolveTaxComponentDescription === 'function'
          ? resolveTaxComponentDescription(entry.rate)
          : ''
      )
    }))
    .filter((entry) => shouldIncludeEntry(entry, includeZeroVatEntries));

const addGroupedEntry = (groupedEntries, entry) => {
  const rate = normalizeVatRate(entry.rate);
  if (!Number.isFinite(rate) || rate < 0) return;

  const grossAmount = Number(entry.grossAmount);
  const vatAmount = Number(entry.vatAmount);
  if (!Number.isFinite(grossAmount) || grossAmount <= 0) return;
  if (!Number.isFinite(vatAmount) || vatAmount < 0) return;

  const key = `${entry.category || '__uncategorized__'}|${rate.toFixed(6)}`;
  const current = groupedEntries.get(key) || {
    category: entry.category || '',
    rate,
    grossAmount: 0,
    vatAmount: 0,
    description: entry.description || ''
  };

  current.grossAmount += grossAmount;
  current.vatAmount += vatAmount;
  if (!current.description && entry.description) {
    current.description = entry.description;
  }

  groupedEntries.set(key, current);
};

const resolveFallbackRate = (transaction, category, resolveFallbackVatRate) => {
  if (typeof resolveFallbackVatRate !== 'function') return null;
  return normalizeVatRate(resolveFallbackVatRate(transaction, category));
};

const resolveStoredRate = (transaction, category, rawRate, sanitizeStoredVatRate) => {
  const rate = normalizeVatRate(rawRate);
  if (!Number.isFinite(rate) || rate < 0) {
    return { rate, wasSanitized: false };
  }

  if (typeof sanitizeStoredVatRate !== 'function') {
    return { rate, wasSanitized: false };
  }

  const sanitizedRate = normalizeVatRate(sanitizeStoredVatRate(transaction, category, rate));
  if (!Number.isFinite(sanitizedRate) || sanitizedRate < 0) {
    return { rate, wasSanitized: false };
  }

  return {
    rate: sanitizedRate,
    wasSanitized: Math.abs(sanitizedRate - rate) > 0.0001
  };
};

const normalizeVatAccountingBasis = (value) => (
  value === 'invoice' ? 'invoice' : 'cash'
);

const getInvoiceBasisGrossAmount = (transaction, txType) => {
  const candidates = txType === 'income'
    ? [
        transaction?.saleTotalAmount,
        transaction?.invoiceTotalAmount,
        transaction?.originalSaleAmount,
        transaction?.grossAmount,
        transaction?.amount
      ]
    : [
        transaction?.expenseTotalAmount,
        transaction?.billTotalAmount,
        transaction?.invoiceTotalAmount,
        transaction?.originalExpenseAmount,
        transaction?.grossAmount,
        transaction?.amount
      ];

  for (const candidate of candidates) {
    const amount = Number.parseFloat(candidate);
    if (Number.isFinite(amount) && amount > 0) {
      return amount;
    }
  }

  return 0;
};

const getVatComponentSource = (transaction, accountingBasis) => {
  if (accountingBasis === 'invoice' && Array.isArray(transaction?.invoiceVatComponents) && transaction.invoiceVatComponents.length > 0) {
    return transaction.invoiceVatComponents;
  }
  return Array.isArray(transaction?.vatComponents) ? transaction.vatComponents : [];
};

const getExplicitVatAmount = (transaction, accountingBasis) => {
  if (accountingBasis === 'invoice') {
    const invoiceVatAmount = Number.parseFloat(transaction?.invoiceVatAmount);
    if (Number.isFinite(invoiceVatAmount) && invoiceVatAmount >= 0) {
      return invoiceVatAmount;
    }
  }
  return Number.parseFloat(transaction?.vatAmount);
};

export const buildTransactionVatEntries = (transaction, options = {}) => {
  if (!transaction || typeof transaction !== 'object') return [];

  const {
    includeZeroVatEntries = false,
    vatAccountingBasis = 'cash',
    resolveFallbackVatRate,
    resolveTaxComponentDescription,
    sanitizeStoredVatRate
  } = options;
  const accountingBasis = normalizeVatAccountingBasis(vatAccountingBasis);
  const groupedEntries = new Map();
  const txType = normalizeTransactionType(transaction.type);
  if (txType === 'supplier_payment') {
    return [];
  }
  const canInferIncomeRate = txType === 'income' && typeof resolveFallbackVatRate === 'function';
  const invoiceBasisGrossAmount = accountingBasis === 'invoice'
    ? getInvoiceBasisGrossAmount(transaction, txType)
    : null;
  const cashBasisGrossAmount = Number.parseFloat(transaction.amount);

  if (
    accountingBasis === 'cash' &&
    (txType === 'income' || txType === 'expenses') &&
    (!Number.isFinite(cashBasisGrossAmount) || cashBasisGrossAmount <= 0)
  ) {
    return [];
  }

  const rctReverseChargeVatAmount = Number.parseFloat(transaction.rctReverseChargeVatAmount);
  const rctReverseChargeVatRate = normalizeVatRate(transaction.rctReverseChargeVatRate ?? 0.135);
  if (transaction.rctReverseCharge && Number.isFinite(rctReverseChargeVatAmount) && rctReverseChargeVatAmount > 0) {
    const grossAmount = getInvoiceBasisGrossAmount(transaction, txType) || Number.parseFloat(transaction.rctGrossAmount) || 0;
    const baseEntry = {
      category: transaction.category || 'subcontractor_rct',
      rate: Number.isFinite(rctReverseChargeVatRate) ? rctReverseChargeVatRate : 0.135,
      vatAmount: roundCurrency(rctReverseChargeVatAmount),
      grossAmount: roundCurrency(grossAmount),
      description: 'RCT reverse-charge VAT'
    };
    return [
      { ...baseEntry, vatBox: 'sales' },
      { ...baseEntry, vatBox: transaction.rctVatInputBlocked ? 'none' : 'purchases' }
    ].filter((entry) => entry.vatBox !== 'none');
  }

  if (Array.isArray(transaction.items) && transaction.items.length > 0) {
    transaction.items.forEach((item) => {
      const grossAmount = accountingBasis === 'invoice'
        ? Number.parseFloat(item?.invoiceAmount ?? item?.saleTotalAmount ?? item?.expenseTotalAmount ?? item?.amount ?? item?.unitPrice ?? 0)
        : Number.parseFloat(item?.amount ?? item?.unitPrice ?? 0);
      const explicitVatAmount = accountingBasis === 'invoice' && item?.invoiceVatAmount !== undefined
        ? Number.parseFloat(item.invoiceVatAmount)
        : Number.parseFloat(item?.vatAmount);
      const category = item?.category || transaction.category || '';
      if (txType === 'income' && isNonTaxableIncomeCategory(category)) {
        return;
      }
      const storedRateResult = resolveStoredRate(transaction, category, item?.vatRate, sanitizeStoredVatRate);
      let rate = storedRateResult.rate;

      if ((!Number.isFinite(rate) || rate < 0) &&
          Number.isFinite(explicitVatAmount) && explicitVatAmount > 0 &&
          Number.isFinite(grossAmount) && grossAmount > explicitVatAmount) {
        rate = normalizeVatRate(explicitVatAmount / (grossAmount - explicitVatAmount));
      }

      if ((!Number.isFinite(rate) || rate < 0) && canInferIncomeRate) {
        rate = resolveFallbackRate(transaction, category, resolveFallbackVatRate);
      }

      let vatAmount = 0;
      if (!storedRateResult.wasSanitized && Number.isFinite(explicitVatAmount) && explicitVatAmount > 0) {
        vatAmount = explicitVatAmount;
      } else if (Number.isFinite(rate) && rate > 0 && Number.isFinite(grossAmount) && grossAmount > 0) {
        vatAmount = calculateVatFromGross(grossAmount, rate);
      }

      addGroupedEntry(groupedEntries, {
        category,
        rate: Number.isFinite(rate) ? rate : null,
        grossAmount,
        vatAmount,
        description: typeof resolveTaxComponentDescription === 'function'
          ? resolveTaxComponentDescription(rate)
          : ''
      });
    });

    const itemEntries = finalizeGroupedEntries(groupedEntries, includeZeroVatEntries, resolveTaxComponentDescription);
    if (shouldUseNestedVatEntries(transaction, itemEntries)) {
      return itemEntries;
    }
    groupedEntries.clear();
  }

  const vatComponents = getVatComponentSource(transaction, accountingBasis);
  if (vatComponents.length > 0) {
    vatComponents.forEach((component) => {
      const category = component?.category || transaction.category || '';
      if (txType === 'income' && isNonTaxableIncomeCategory(category)) {
        return;
      }

      const storedRateResult = resolveStoredRate(transaction, category, component?.rate, sanitizeStoredVatRate);
      const rate = storedRateResult.rate;
      const vatAmount = Number.parseFloat(component?.amount);
      const fallbackGrossAmount =
        Number.isFinite(vatAmount) && vatAmount >= 0 && Number.isFinite(rate) && rate > 0
          ? calculateGrossFromVat(vatAmount, rate)
          : (accountingBasis === 'invoice'
              ? invoiceBasisGrossAmount
              : Number.parseFloat(transaction.amount ?? 0));
      const grossAmount = Number.isFinite(Number(component?.grossAmount))
        ? Number.parseFloat(component.grossAmount)
        : fallbackGrossAmount;

      addGroupedEntry(groupedEntries, {
        category,
        rate,
        grossAmount,
        vatAmount: storedRateResult.wasSanitized && Number.isFinite(rate) && Number.isFinite(grossAmount) && grossAmount > 0
          ? calculateVatFromGross(grossAmount, rate)
          : (Number.isFinite(vatAmount) && vatAmount >= 0 ? vatAmount : 0),
        description: component?.description || (
          typeof resolveTaxComponentDescription === 'function'
            ? resolveTaxComponentDescription(rate)
            : ''
        )
      });
    });

    const componentEntries = finalizeGroupedEntries(groupedEntries, includeZeroVatEntries, resolveTaxComponentDescription);
    if (shouldUseNestedVatEntries(transaction, componentEntries)) {
      return componentEntries;
    }
    groupedEntries.clear();
  }

  const amount = accountingBasis === 'invoice' && (txType === 'income' || txType === 'expenses')
    ? invoiceBasisGrossAmount
    : Number.parseFloat(transaction.amount);
  const explicitVatAmount = getExplicitVatAmount(transaction, accountingBasis);
  if (txType === 'income' && isNonTaxableIncomeCategory(transaction.category)) {
    return [];
  }
  const storedRateResult = resolveStoredRate(transaction, transaction.category || '', transaction.vatRate, sanitizeStoredVatRate);
  let rate = storedRateResult.rate;

  if ((!Number.isFinite(rate) || rate < 0) &&
      Number.isFinite(explicitVatAmount) && explicitVatAmount > 0 &&
      Number.isFinite(amount) && amount > explicitVatAmount) {
    rate = normalizeVatRate(explicitVatAmount / (amount - explicitVatAmount));
  }

  if ((!Number.isFinite(rate) || rate < 0) && canInferIncomeRate) {
    rate = resolveFallbackRate(transaction, transaction.category || '', resolveFallbackVatRate);
  }

  let vatAmount = 0;
  if (!storedRateResult.wasSanitized && Number.isFinite(explicitVatAmount) && explicitVatAmount > 0) {
    vatAmount = explicitVatAmount;
  } else if (Number.isFinite(rate) && rate > 0 && Number.isFinite(amount) && amount > 0) {
    vatAmount = calculateVatFromGross(amount, rate);
  }

  const directEntry = {
    category: transaction.category || '',
    rate: Number.isFinite(rate) ? rate : null,
    grossAmount: Number.isFinite(amount) && amount > 0
      ? amount
      : (Number.isFinite(vatAmount) && vatAmount >= 0 ? calculateGrossFromVat(vatAmount, rate) : 0),
    vatAmount,
    description: typeof resolveTaxComponentDescription === 'function'
      ? resolveTaxComponentDescription(rate)
      : ''
  };

  return shouldIncludeEntry(directEntry, includeZeroVatEntries)
    ? [{
        category: directEntry.category,
        rate: directEntry.rate,
        vatAmount: roundCurrency(directEntry.vatAmount),
        grossAmount: roundCurrency(directEntry.grossAmount),
        description: directEntry.description
      }]
    : [];
};

export const getTransactionVatAmount = (transaction, options = {}) => {
  const txType = normalizeTransactionType(transaction?.type);
  if (txType === 'supplier_payment') {
    return 0;
  }

  const vatEntries = buildTransactionVatEntries(transaction, options);
  if (vatEntries.length > 0) {
    return roundCurrency(vatEntries.reduce((sum, entry) => sum + (Number(entry?.vatAmount) || 0), 0));
  }

  if (txType === 'income' && isNonTaxableIncomeCategory(transaction?.category)) {
    return 0;
  }

  const accountingBasis = normalizeVatAccountingBasis(options?.vatAccountingBasis);
  const basisGrossAmount = accountingBasis === 'invoice' && (txType === 'income' || txType === 'expenses')
    ? getInvoiceBasisGrossAmount(transaction, txType)
    : Number.parseFloat(transaction?.amount);
  const explicitVatAmount = accountingBasis === 'invoice'
    ? getExplicitVatAmount(transaction, accountingBasis)
    : Number(transaction?.vatAmount);
  if (Number.isFinite(explicitVatAmount) && explicitVatAmount > 0) {
    if (accountingBasis === 'cash' && (!Number.isFinite(basisGrossAmount) || basisGrossAmount <= 0)) {
      return 0;
    }
    return roundCurrency(explicitVatAmount);
  }

  return 0;
};
